// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// VIEW 8 — "The record".
//
// The question this view answers is the narrowest and the most practical on the
// site: what does the register actually say about ONE record, in its own words,
// and where do I go to read the authority for it?
//
// So this is the plain part of the site. No chart, no map, no derived measure —
// a searchable list across all six registers, and behind every row the record
// card, with every field spelled out in words a reader who has never met native
// title law can follow. It is the primary home of the verbatim register text:
// the description of who holds native title, the full Federal Court case name
// and every corporation name are reproduced exactly as published, with no
// clamp, no ellipsis and no title-casing at any width.
//
// THREE THINGS THIS VIEW DELIBERATELY DOES NOT DO.
//
//   * It never orders holders, corporations or country against one another.
//     Sorting is by date, area, name or jurisdiction — administrative facts —
//     and the corporation register can only be ordered by name, because a sort
//     by "how much they hold" would set Aboriginal and Torres Strait Islander
//     groups against one another inside a table header.
//
//   * It never sums the area column, and says so in a caption directly above
//     the table. The register's per-record areas overlap; on the agreement
//     register the column adds up to more than twice the ground it covers.
//     Every area figure elsewhere on this site is a dissolved union.
//
//   * It never renders a status as a tick, a cross or a bare colour. Every
//     swatch carries its category name in words, live matters are unfilled
//     outlines because an assertion under consideration is not a finding, and
//     a field the register does not record is its own third state.

import { FINDING, FINDING_ORDER, METHOD, PENDING_STROKE, UNRECORDED, subjectColour, type Finding } from '../theme';
import { esc, num, km2, longDate, shortDate, duration, tipAttr, plural } from '../format';
import { openDetermination, indexOutcomes, indexHolders } from '../components/detail';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { currentRoute, setParams } from '../router';
import {
  getMeta, getDetermination, getDeterminations,
  loadJurisdictions, loadMatters, loadAgreements, loadHolders, loadRatsib, loadOutcomes,
} from '../data';
import type {
  Determination, Matter, Agreement, Holder, Ratsib, OutcomePolygonRow,
} from '../types';

type RecordType = 'determinations' | 'claims' | 'applications' | 'agreements' | 'corporations' | 'regions';
type SortKey = 'date' | 'name' | 'area' | 'juris';

interface Item {
  /** The value a shared `?id=` link carries. */
  key: string;
  name: string;
  /** Every jurisdiction this record touches — a corporation can hold country in more than one. */
  jurisAll: string[];
  juris: string | null;
  date: string | null;
  area: number | null;
  finding: Finding | null;
  /** Lower-cased searchable text. */
  hay: string;
  cells: (q: string) => string;
  open: () => void;
}

interface TypeDef {
  id: RecordType;
  label: string;
  noun: string;
  nouns: string;
  blurb: string;
  sorts: SortKey[];
  defaultSort: SortKey;
  defaultDir: 1 | -1;
  headers: { label: string; key?: SortKey; cls?: string; tip?: string }[];
  areaCaption: string;
  items: Item[];
}

const PAGE = 200;
const STORE_TYPE = 'au-native-title:register:type';
const STORE_SORT = 'au-native-title:register:sort';
const STORE_DIR = 'au-native-title:register:dir';

/** The note that appears in the footer of every record card on this site. */
const RECORD_FOOTER = `<div class="note legal xsmall">The boundaries behind every area shown here are
  <strong>indicative only</strong>. They are not cadastral or legal boundaries, they cannot be used to
  establish rights over any particular parcel of land or water, and nothing on this site is legal advice.
  The Federal Court judgment, and the National Native Title Tribunal’s own registers, are the
  authoritative record.</div>`;

/* ───────────────────────────── small helpers ─────────────────────────────── */

function store(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function remember(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode — the session still works */ }
}

/** Highlight the matched substring, escaping every piece on the way out. */
function hl(text: string | null | undefined, q: string): string {
  const s = String(text ?? '');
  if (!s) return '';
  if (!q) return esc(s);
  const lower = s.toLowerCase();
  let out = '';
  let i = 0;
  for (;;) {
    const j = lower.indexOf(q, i);
    if (j < 0) { out += esc(s.slice(i)); break; }
    out += `${esc(s.slice(i, j))}<mark class="reg-hit">${esc(s.slice(j, j + q.length))}</mark>`;
    i = j + q.length;
  }
  return out;
}

function field(label: string, valueHtml: string): string {
  if (!valueHtml) return '';
  return `<div class="detail-field"><div class="detail-label">${esc(label)}</div><div>${valueHtml}</div></div>`;
}

function nameCell(nameHtml: string, subHtml = ''): string {
  return `<button type="button" class="reg-open verbatim">${nameHtml}</button>${subHtml}`;
}

/**
 * The outcome swatch: hue AND texture AND the category name in words, never an
 * icon, a tick or a cross. "Exists in parts" carries the stipple, the same
 * texture the non-exclusive class carries everywhere else on the site.
 */
function findingSwatch(f: Finding | null, findingText: string | null): string {
  const style = f ? FINDING[f] : null;
  const texture = f === 'parts'
    ? 'background-image:radial-gradient(rgba(255,255,255,.75) 1px, transparent 1px);background-size:4px 4px;'
    : '';
  const words = style?.label ?? findingText ?? 'Outcome not recorded';
  const t = tipAttr(
    style ? style.register : (findingText ?? 'The register does not record an outcome for this determination'),
    style?.plain,
    'Open the record for the court’s own words.',
  );
  return `<span class="reg-status" data-tip="${t}">
    <span class="legend-swatch reg-swatch" style="background:${style?.fill ?? UNRECORDED};${texture}"></span>
    <span>${esc(words)}</span></span>`;
}

/** Live matters are UNFILLED outlines. An assertion under consideration is not a finding. */
function pendingSwatch(words: string, tipText: string): string {
  return `<span class="reg-status" data-tip="${tipAttr(tipText)}">
    <span class="legend-swatch reg-swatch reg-outline" style="border-color:${PENDING_STROKE}"></span>
    <span class="verbatim reg-inline">${esc(words)}</span></span>`;
}

function dateCell(iso: string | null, meaning: string): string {
  if (!iso) return `<span class="muted" data-tip="${tipAttr('Not recorded on this register.')}">Not recorded</span>`;
  return `<span data-tip="${tipAttr(longDate(iso), meaning)}">${esc(shortDate(iso))}</span>`;
}

function areaCell(area: number | null, meaning: string): string {
  if (area == null) return `<span class="muted" data-tip="${tipAttr('Not recorded on this register.')}">Not recorded</span>`;
  const t = tipAttr(
    `${km2(area, { exact: true })} as stated on the register`,
    meaning,
    'Adding this column double-counts ground where records overlap.',
  );
  return `<span data-tip="${t}">${esc(km2(area))}</span>`;
}

/* ─────────────────────────── plain-English lookups ───────────────────────── */

function applicationTypePlain(t: string | null): string {
  switch (t) {
    case 'Claimant':
      return 'A claimant application: a group asks the Federal Court to determine that native title exists over an area, who holds it, and what it allows them to do.';
    case 'Non-Claimant':
      return 'A non-claimant application: someone who does not assert native title — commonly a landholder or a government — asks the Court to determine whether native title exists over an area.';
    case 'Compensation':
      return 'A compensation application: it asks the Court to decide whether compensation is payable because native title was extinguished or impaired by an act of government.';
    case 'Revised Native Title Determination':
      return 'An application to revise or revoke a determination that has already been made.';
    default:
      return 'The register does not record the type of this application.';
  }
}

function registrationPlain(status: string | null): string {
  const s = status ?? '';
  const progress = /new decision in progress/i.test(s)
    ? ' A new registration decision is currently in progress, so this wording may change.'
    : '';
  if (/^Accepted/i.test(s)) {
    return `The application has met the conditions of the registration test in section 190A, so it is entered on the Register of Native Title Claims. Registration carries procedural rights — most importantly the right to negotiate about mining and other future acts.${progress}`;
  }
  if (/^Not Accepted/i.test(s)) {
    return `The application has not met the conditions of the registration test. That is a threshold about the application as filed, not a decision about the merits of the claim: it can be amended and tested again.${progress}`;
  }
  if (/^Currently identified/i.test(s)) {
    return `The Registrar is currently making a registration decision about this application.${progress}`;
  }
  if (/^Not currently identified/i.test(s)) {
    return `No registration decision is being made about this application at the moment. It remains a live application before the Federal Court.${progress}`;
  }
  return 'The register does not record a registration decision for this application.';
}

function agreementTypePlain(t: string | null): string {
  if (t === 'Area Agreement') {
    return 'An area agreement is made where a registered native title body corporate does not exist for the whole of the area. It is negotiated with the registered native title claimants and the other native title parties for that country.';
  }
  if (t === 'Body Corporate') {
    return 'A body corporate agreement is made where registered native title bodies corporate exist for the entire area, and every one of them is a party to it.';
  }
  return 'The register does not record the agreement type.';
}

function methodCell(m: Determination['method']): string {
  if (!m) return `<span class="muted" data-tip="${tipAttr('The register does not record how this determination was reached.')}">Not recorded</span>`;
  const style = METHOD[m];
  return `<span data-tip="${tipAttr(style.label, style.plain)}">${esc(style.label)}</span>`;
}

/** Yes / No / Not recorded — three states, and the third is never folded into the second. */
function threeState(label: string, v: boolean | null, meaning: string): string {
  if (v == null) {
    return field(label, `<span class="reg-status">
      <span class="legend-swatch reg-swatch" style="background:${UNRECORDED}"></span>
      <span>Not recorded</span></span>
      <div class="xsmall muted">The register does not state this either way. It is its own state here, and is never counted as a “no”.</div>`);
  }
  return field(label, `<strong>${v ? 'Yes' : 'No'}</strong>
    <div class="xsmall muted">${esc(meaning)}</div>`);
}

/**
 * The outcome polygons, for the per-polygon breakdown inside the determination
 * panel. Goes through the shared loader so that a reader who visits both this
 * view and the map downloads the file once rather than twice — `loadOutcomes`
 * keeps one in-flight promise per file. A failure here only costs the breakdown
 * block, so the caller swallows it.
 */
async function loadOutcomeRows(): Promise<OutcomePolygonRow[]> {
  return loadOutcomes();
}

/* ───────────────────────────── the record card ───────────────────────────── */

function openRecord(opts: { key: string; kind: string; title: string; body: string }): void {
  openOverlay({
    panelClass: 'drawer',
    label: `${opts.kind}: ${opts.title}`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.className = 'verbatim';
      h.textContent = opts.title;
      head.append(h, closeButton(close, `Close this ${opts.kind.toLowerCase()}`));

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `${opts.body}${RECORD_FOOTER}`;

      // Cross-links to determinations, wired after the markup exists.
      for (const btn of body.querySelectorAll<HTMLButtonElement>('[data-det]')) {
        btn.addEventListener('click', () => {
          const d = getDetermination(btn.dataset.det ?? '');
          if (!d) return;
          close();
          openDetermination(d);
        });
      }

      panel.append(head, body);
    },
    onClose() { setParams({ id: null }); },
  });
  setParams({ id: opts.key });
}

/* ────────────────────────────────── view ─────────────────────────────────── */

export async function renderRegister(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const determinations = getDeterminations();
  const [jurisRows, matters, agreementsFile, holders, ratsib] = await Promise.all([
    loadJurisdictions(), loadMatters(), loadAgreements(), loadHolders(), loadRatsib(),
  ]);

  indexHolders(holders);
  try {
    indexOutcomes(await loadOutcomeRows());
  } catch {
    // The determination panel simply omits the per-polygon breakdown.
  }

  const jurisName = new Map(jurisRows.map((j) => [j.code, j.name]));
  const jurisLand = new Map(jurisRows.map((j) => [j.code, j.landKm2]));
  const jurisOrder = [...jurisRows].sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
  const datasetUrl = (key: string) => meta.sources.find((s) => s.key === key)?.dataset ?? null;

  const jurisCell = (code: string | null): string => {
    if (!code) return '<span class="muted">—</span>';
    const land = jurisLand.get(code);
    const t = tipAttr(jurisName.get(code) ?? code, land ? `${num(land)} km² of land` : undefined);
    return `<span data-tip="${t}">${esc(code)}</span>`;
  };
  const jurisListCell = (codes: string[]): string => (codes.length
    ? codes.map(jurisCell).join(' ')
    : `<span class="muted" data-tip="${tipAttr('This corporation holds no determination on the register with a jurisdiction recorded.')}">—</span>`);

  const holdersByDet = new Map<string, Holder[]>();
  for (const h of holders) {
    for (const id of h.determinations) {
      if (!holdersByDet.has(id)) holdersByDet.set(id, []);
      holdersByDet.get(id)!.push(h);
    }
  }

  const ratsibByOrg = new Map<string, Ratsib>();
  for (const r of ratsib) {
    const org = (r.org ?? '').toLowerCase().replace(/\b(ltd|limited|pty)\b\.?/g, '').replace(/[^a-z]+/g, '');
    if (org && !ratsibByOrg.has(org)) ratsibByOrg.set(org, r);
  }
  const repLink = (rep: string | null): string | null => {
    if (!rep) return null;
    const k = rep.toLowerCase().replace(/\b(ltd|limited|pty)\b\.?/g, '').replace(/[^a-z]+/g, '');
    return ratsibByOrg.get(k)?.link ?? null;
  };

  /* ── determinations ── */

  const detItems: Item[] = determinations.map((d): Item => {
    const name = d.name ?? d.id;
    const held = holdersByDet.get(d.id) ?? [];
    const hay = [
      name, d.caseName, d.caseNo, d.holders, d.bodyCorporate, d.judge, d.id, d.juris, d.findingText,
      d.method ? METHOD[d.method].label : '', ...held.map((h) => h.name),
    ].filter(Boolean).join(' ').toLowerCase();
    return {
      key: d.id,
      name,
      jurisAll: [d.juris],
      juris: d.juris,
      date: d.date,
      area: d.areaSqKm,
      finding: d.finding,
      hay,
      open: () => openDetermination(d),
      cells: (q) => `
        <td class="reg-c-name">${nameCell(hl(name, q), d.caseName ? `<span class="reg-sub verbatim">${hl(d.caseName, q)}</span>` : '')}</td>
        <td data-label="What the court found">${findingSwatch(d.finding, d.findingText)}</td>
        <td data-label="How it was decided">${methodCell(d.method)}</td>
        <td data-label="Jurisdiction">${jurisCell(d.juris)}</td>
        <td class="num" data-label="Determination date">${dateCell(d.date, 'The day the Federal Court made the determination.')}</td>
        <td class="num" data-label="Determination area">${areaCell(d.areaSqKm, 'The area the court considered — not the area over which native title was recognised.')}</td>`,
    };
  });

  /* ── live matters, split into the two registers they sit on ── */

  const matterItem = (m: Matter): Item => {
    const name = m.name ?? m.id;
    const hay = [name, m.id, m.representative, m.applicationType, m.registrationStatus, m.juris, m.status]
      .filter(Boolean).join(' ').toLowerCase();
    return {
      key: m.onRegister ? `REG:${m.id}` : m.id,
      name,
      jurisAll: [m.juris],
      juris: m.juris,
      date: m.lodged,
      area: m.areaSqKm,
      finding: null,
      hay,
      open: () => openMatter(m),
      cells: (q) => `
        <td class="reg-c-name">${nameCell(hl(name, q), `<span class="reg-sub mono">${hl(m.id, q)}</span>`)}</td>
        <td data-label="Registration test">${pendingSwatch(m.registrationStatus ?? 'Not recorded', `${m.registrationStatus ?? 'Not recorded'}\nA live application, not a finding: the Court has not determined whether native title exists here.`)}</td>
        <td data-label="Application type">${hl(m.applicationType, q)}</td>
        <td data-label="Jurisdiction">${jurisCell(m.juris)}</td>
        <td class="num" data-label="Lodged">${dateCell(m.lodged, `Lodged with the Federal Court. Running ${duration(m.daysSinceLodged)} so far.`)}</td>
        <td class="num" data-label="Application area">${areaCell(m.areaSqKm, 'The area applied for — not an area over which anything has been recognised.')}</td>`,
    };
  };

  const openMatter = (m: Matter): void => {
    const link = repLink(m.representative);
    const ds = datasetUrl(m.onRegister ? 'claims' : 'schedule');
    openRecord({
      key: m.onRegister ? `REG:${m.id}` : m.id,
      kind: m.onRegister ? 'Registered claim' : 'Live application',
      title: m.name ?? m.id,
      body: `
        <div class="detail-headline">
          ${pendingSwatch('Live matter — undecided', 'Nothing on this record is a finding. The Federal Court has not determined whether native title exists over this area.')}
          <span class="pill">${esc(m.applicationType ?? 'Application')}</span>
          <span class="pill">${m.onRegister ? 'On the Register of Native Title Claims' : 'On the Schedule of Applications'}</span>
          ${m.sea ? '<span class="pill">Includes sea country</span>' : ''}
        </div>
        <p class="small muted">This is an application before the Federal Court, shown here exactly as the
          Tribunal’s register states it. It asserts something; it decides nothing.</p>

        <div class="detail-block">
          <h3>What kind of application this is</h3>
          <p class="small">${esc(applicationTypePlain(m.applicationType))}</p>
        </div>

        <div class="detail-block">
          <h3>The registration test${glossIcon('registration-test')}</h3>
          <div class="detail-field">
            <div class="detail-label">The register’s own wording</div>
            <div class="verbatim small">${esc(m.registrationStatus ?? 'Not recorded')}</div>
          </div>
          <p class="small muted">${esc(registrationPlain(m.registrationStatus))}</p>
        </div>

        <div class="detail-block">
          <h3>The matter</h3>
          <div class="detail-grid">
            ${field('Tribunal reference', `<span class="mono">${esc(m.id)}</span>`)}
            ${field('Jurisdiction', esc(jurisName.get(m.juris) ?? m.juris))}
            ${field('Status on the register', esc(m.status ?? 'Not recorded'))}
            ${field('Lodged with the Court', esc(longDate(m.lodged)))}
            ${field('Entered on the claims register', m.registeredOn
              ? `${esc(longDate(m.registeredOn))} <span class="xsmall muted">— ${esc(duration(m.daysToRegister))} after it was lodged</span>`
              : 'Not on the Register of Native Title Claims')}
            ${field('Notification period closed', m.notificationClosed ? esc(longDate(m.notificationClosed)) : 'Not recorded')}
            ${field('Most recent Federal Court order', m.courtOrder ? esc(longDate(m.courtOrder)) : 'None recorded')}
            ${field('Running time', `${esc(duration(m.daysSinceLodged))} <span class="xsmall muted">— since it was lodged. These registers hold only live matters, so no finished duration can be read from them.</span>`)}
            ${field('Application area', `${esc(km2(m.areaSqKm, { exact: true }))} <span class="xsmall muted">— the area applied for, as stated on the register</span>`)}
            ${field('Sea country', m.sea ? 'The application includes sea country' : 'Land only')}
          </div>
        </div>

        <div class="detail-block">
          <h3>Who is assisting${glossIcon('ratsib')}</h3>
          ${m.representative
            ? `<div class="verbatim">${esc(m.representative)}</div>`
            : '<p class="small muted">The register does not record a representative for this application.</p>'}
          ${link ? `<p class="small"><a href="${esc(link)}" target="_blank" rel="noopener">Visit their website ↗</a></p>` : ''}
        </div>

        ${ds ? `<div class="detail-actions"><a class="btn" href="${esc(ds)}" target="_blank" rel="noopener">Open the register this record comes from ↗</a></div>` : ''}
      `,
    });
  };

  const claimItems = matters.filter((m) => m.onRegister).map(matterItem);
  const scheduleItems = matters.filter((m) => !m.onRegister).map(matterItem);

  /* ── agreements ── */

  const subjectIndex = new Map(agreementsFile.subjects.map((s, i) => [s.top, i]));

  const openAgreement = (a: Agreement): void => {
    const link = repLink(a.representative);
    const ds = datasetUrl('agreements');
    const chips = a.subjects.map((s) => {
      const c = subjectColour(subjectIndex.get(s.top) ?? 0);
      const subs = s.subs.length ? `<span class="xsmall muted"> — ${esc(s.subs.join(', '))}</span>` : '';
      return `<div class="reg-subject"><span class="legend-swatch reg-swatch" style="background:${c}"></span>
        <span><strong>${esc(s.top)}</strong>${subs}</span></div>`;
    }).join('');
    openRecord({
      key: a.id,
      kind: 'Indigenous Land Use Agreement',
      title: a.name ?? a.id,
      body: `
        <div class="detail-headline">
          <span class="pill">${esc(a.status ?? 'Status not recorded')}</span>
          <span class="pill">${esc(a.agreementType ?? 'Type not recorded')}</span>
          ${a.certified ? '<span class="pill">Certified</span>' : ''}
          ${a.sea ? '<span class="pill">Includes sea country</span>' : ''}
        </div>
        <p class="small muted">An ${glossTerm('ilua', 'Indigenous Land Use Agreement')} is a voluntary,
          legally binding agreement about how land and waters will be used. Once registered it binds
          everyone who holds native title in the area, including people who were not at the table.</p>

        <div class="detail-block">
          <h3>What kind of agreement this is</h3>
          <p class="small">${esc(agreementTypePlain(a.agreementType))}</p>
        </div>

        <div class="detail-block">
          <h3>What it is about</h3>
          ${chips ? `<div class="reg-subjects">${chips}</div>` : '<p class="small muted">The register records no subject matter for this agreement.</p>'}
          ${a.subjectText ? `<div class="detail-field"><div class="detail-label">The register’s own wording</div><div class="verbatim small">${esc(a.subjectText)}</div></div>` : ''}
        </div>

        <div class="detail-block">
          <h3>What the agreement does</h3>
          <p class="xsmall muted">Each of these is recorded as yes, no, or not recorded at all. A blank is
            shown as its own state and is never read as a no.</p>
          <div class="detail-grid">
            ${threeState('Consent to future acts', a.consentToFutureAct, 'The register records whether the agreement consents to acts that would otherwise affect native title.')}
            ${threeState('Right to negotiate', a.rightToNegotiate, 'The register records whether the agreement covers acts to which the right to negotiate would otherwise apply.')}
            ${threeState('Surrender of native title', a.surrender, 'The register records whether the agreement includes a surrender of native title over some or all of the area.')}
            ${threeState('Validates a future act', a.validatesFutureAct, 'The register records whether the agreement validates an act already done.')}
            ${threeState('Intermediate period acts', a.intermediatePeriodActs, 'The register records whether the agreement deals with acts done between 1 January 1994 and 23 December 1996.')}
          </div>
        </div>

        <div class="detail-block">
          <h3>The record</h3>
          <div class="detail-grid">
            ${field('Tribunal reference', `<span class="mono">${esc(a.id)}</span>`)}
            ${field('Jurisdiction', esc(jurisName.get(a.juris) ?? a.juris))}
            ${field('Lodged', esc(longDate(a.lodged)))}
            ${field('Notification began', esc(longDate(a.notified)))}
            ${field('Entered on the register', a.registeredOn ? esc(longDate(a.registeredOn)) : 'Not yet — this agreement is in notification')}
            ${field('Agreement starts', a.start ? esc(longDate(a.start)) : 'Not recorded')}
            ${field('Agreement ends', a.end ? esc(longDate(a.end)) : 'Not recorded — no end date is stated')}
            ${field('Authorisation', a.certified
              ? 'Certified by the representative body for the area'
              : 'The register records an authorisation basis other than certification by the representative body')}
            ${field('Agreement area', `${esc(km2(a.areaSqKm, { exact: true }))} <span class="xsmall muted">— as stated on the register. Agreement areas overlap heavily; this figure cannot be added to another.</span>`)}
            ${field('Sea country', a.sea ? 'The agreement includes sea country' : 'Land only')}
          </div>
        </div>

        <div class="detail-block">
          <h3>Representative body or agent${glossIcon('ratsib')}</h3>
          ${a.representative
            ? `<div class="verbatim">${esc(a.representative)}</div>`
            : '<p class="small muted">The register does not record one for this agreement.</p>'}
          ${link ? `<p class="small"><a href="${esc(link)}" target="_blank" rel="noopener">Visit their website ↗</a></p>` : ''}
          <p class="xsmall muted">The applicant field on this register names private individuals. It is not
            downloaded by this site and does not appear anywhere on it.</p>
        </div>

        ${ds ? `<div class="detail-actions"><a class="btn" href="${esc(ds)}" target="_blank" rel="noopener">Open the register this record comes from ↗</a></div>` : ''}
      `,
    });
  };

  const agreementItems: Item[] = agreementsFile.rows.map((a): Item => {
    const name = a.name ?? a.id;
    const tops = a.subjects.map((s) => s.top).join(', ');
    const hay = [name, a.id, a.representative, a.agreementType, a.subjectText, a.juris, a.status]
      .filter(Boolean).join(' ').toLowerCase();
    return {
      key: a.id,
      name,
      jurisAll: [a.juris],
      juris: a.juris,
      date: a.registeredOn ?? a.notified,
      area: a.areaSqKm,
      finding: null,
      hay,
      open: () => openAgreement(a),
      cells: (q) => `
        <td class="reg-c-name">${nameCell(hl(name, q), `<span class="reg-sub mono">${hl(a.id, q)}</span>`)}</td>
        <td data-label="Agreement type"><span data-tip="${tipAttr(agreementTypePlain(a.agreementType))}">${esc(a.agreementType ?? 'Not recorded')}</span></td>
        <td data-label="Subject matter"><span class="reg-sub-wrap" data-tip="${tipAttr(a.subjectText ?? 'No subject matter recorded')}">${hl(tops || 'Not recorded', q)}</span></td>
        <td data-label="Jurisdiction">${jurisCell(a.juris)}</td>
        <td class="num" data-label="Entered on the register">${dateCell(a.registeredOn, a.registeredOn ? 'The day the agreement went on the register.' : 'In notification — not yet registered.')}</td>
        <td class="num" data-label="Agreement area">${areaCell(a.areaSqKm, 'The area of this agreement as stated on the register. Agreement areas overlap more than any other layer on this site.')}</td>`,
    };
  });

  /* ── corporations ── */

  const openCorporation = (h: Holder): void => {
    const dets = h.determinations
      .map((id) => ({ id, d: getDetermination(id) }))
      .sort((a, b) => (b.d?.date ?? '').localeCompare(a.d?.date ?? ''));
    const list = dets.map(({ id, d }) => `<li>
      ${d ? `<button type="button" class="reg-link verbatim" data-det="${esc(id)}">${esc(d.name ?? id)}</button>
             <span class="xsmall muted">${esc(longDate(d.date))} · ${esc(d.juris)}</span>`
          : `<span class="mono">${esc(id)}</span> <span class="xsmall muted">— this determination is not on the register file this site loads</span>`}
    </li>`).join('');
    openRecord({
      key: `ICN${h.icn ?? h.name}`,
      kind: 'Registered native title body corporate',
      title: h.name,
      body: `
        <div class="detail-headline">
          <span class="pill">Registered native title body corporate</span>
          <span class="pill">${esc(plural(h.determinations.length, 'determination'))}</span>
        </div>
        <p class="small muted">A ${glossTerm('rntbc', 'registered native title body corporate')} holds or
          manages native title on behalf of the holders after a determination. It is the entity others must
          deal with about that country.</p>

        <div class="detail-block">
          <h3>The corporation</h3>
          <div class="detail-field">
            <div class="detail-label">Name, exactly as registered</div>
            <div class="verbatim">${esc(h.name)}</div>
          </div>
          <div class="detail-grid">
            ${field('Indigenous Corporation Number (ICN)', `<span class="mono">${esc(h.icn ?? 'Not recorded')}</span>`)}
            ${field('Determinations it holds or manages', esc(plural(h.determinations.length, 'determination')))}
          </div>
        </div>

        <div class="detail-block">
          <h3>Determinations</h3>
          <p class="xsmall muted">Listed most recent first. This is a list of records, not a measure of
            anything — open any one of them to read what the court found.</p>
          <ul class="plain-list">${list}</ul>
        </div>

        ${h.register ? `<div class="detail-actions"><a class="btn" href="${esc(h.register)}" target="_blank" rel="noopener">Open its entry on the ORIC public register ↗</a></div>`
          : '<p class="small muted">The register does not record a link to this corporation’s entry on the Office of the Registrar of Indigenous Corporations public register.</p>'}
      `,
    });
  };

  const corporationItems: Item[] = holders.map((h): Item => {
    const codes = [...new Set(h.determinations.map((id) => getDetermination(id)?.juris).filter((x): x is string => !!x))].sort();
    const hay = [h.name, h.icn, ...h.determinations].filter(Boolean).join(' ').toLowerCase();
    return {
      key: `ICN${h.icn ?? h.name}`,
      name: h.name,
      jurisAll: codes,
      juris: codes[0] ?? null,
      date: null,
      area: null,
      finding: null,
      hay,
      open: () => openCorporation(h),
      cells: (q) => `
        <td class="reg-c-name">${nameCell(hl(h.name, q))}</td>
        <td class="mono" data-label="ICN">${hl(h.icn, q)}</td>
        <td data-label="Determinations held">${esc(plural(h.determinations.length, 'determination'))}</td>
        <td data-label="Jurisdictions">${jurisListCell(codes)}</td>
        <td data-label="Corporate register">${h.register
          ? `<a href="${esc(h.register)}" target="_blank" rel="noopener">ORIC ↗</a>`
          : '<span class="muted">Not recorded</span>'}</td>`,
    };
  });

  /* ── representative regions ── */

  const openRegion = (r: Ratsib): void => {
    const kindPlain = r.kind === 'Native Title Representative Body'
      ? 'A representative body is recognised under section 203AD of the Native Title Act for a defined region.'
      : r.kind === 'Native Title Service Provider'
        ? 'A service provider is funded under section 203FE of the Native Title Act to perform the same functions in a region where no representative body is recognised.'
        : 'The register records no recognised body for this region.';
    openRecord({
      key: r.id ?? (r.name ?? 'region'),
      kind: 'Representative region',
      title: r.name ?? (r.org ?? 'Representative region'),
      body: `
        <div class="detail-headline">
          <span class="pill">${esc(r.kind ?? 'No recognised body')}</span>
          <span class="pill">${esc(jurisName.get(r.juris ?? '') ?? r.juris ?? 'Jurisdiction not recorded')}</span>
        </div>
        <p class="small muted">${glossTerm('ratsib', 'Representative bodies and service providers')} research
          connection, prepare and run applications, certify agreements and support bodies corporate. There are
          ${esc(num(meta.counts.representativeRegions))} such regions.</p>

        <div class="detail-block">
          <h3>The organisation</h3>
          <div class="detail-field">
            <div class="detail-label">Name, exactly as recorded</div>
            <div class="verbatim">${esc(r.org ?? 'No recognised body')}</div>
          </div>
          <p class="small muted">${esc(kindPlain)}</p>
        </div>

        <div class="detail-block">
          <h3>The record</h3>
          <div class="detail-grid">
            ${field('Region', esc(r.name ?? 'Not recorded'))}
            ${field('Jurisdiction', esc(jurisName.get(r.juris ?? '') ?? r.juris ?? 'Not recorded'))}
            ${field('Kind', esc(r.kind ?? 'No recognised body'))}
            ${field('Recognised under', esc(r.authority ?? 'Not recorded'))}
            ${field('Tribunal reference', `<span class="mono">${esc(r.id ?? '—')}</span>`)}
          </div>
          ${r.comments ? `<div class="detail-field"><div class="detail-label">Notes on the register</div><div class="verbatim small">${esc(r.comments)}</div></div>` : ''}
        </div>

        ${r.link ? `<div class="detail-actions"><a class="btn" href="${esc(r.link)}" target="_blank" rel="noopener">Visit the organisation’s website ↗</a></div>`
          : '<p class="small muted">No website is recorded for this region.</p>'}
      `,
    });
  };

  const regionItems: Item[] = ratsib.map((r): Item => {
    const name = r.name ?? (r.org ?? 'Region');
    const hay = [name, r.org, r.kind, r.juris, r.authority, r.comments, r.id].filter(Boolean).join(' ').toLowerCase();
    return {
      key: r.id ?? name,
      name,
      jurisAll: r.juris ? [r.juris] : [],
      juris: r.juris,
      date: null,
      area: null,
      finding: null,
      hay,
      open: () => openRegion(r),
      cells: (q) => `
        <td class="reg-c-name">${nameCell(hl(name, q))}</td>
        <td data-label="Organisation"><span class="verbatim reg-inline">${hl(r.org, q)}</span></td>
        <td data-label="Kind">${r.kind ? hl(r.kind, q) : '<span class="muted">No recognised body</span>'}</td>
        <td data-label="Jurisdiction">${jurisCell(r.juris)}</td>
        <td data-label="Website">${r.link
          ? `<a href="${esc(r.link)}" target="_blank" rel="noopener">Website ↗</a>`
          : '<span class="muted">None recorded</span>'}</td>`,
    };
  });

  /* ── the six registers ── */

  const areaCaptionCommon = 'Do not add this column up. The register’s per-record areas overlap, so a sum counts the same ground more than once.';
  const DEFS: Record<RecordType, TypeDef> = {
    determinations: {
      id: 'determinations',
      label: 'Determinations',
      noun: 'determination',
      nouns: 'determinations',
      blurb: 'Every decision of the Federal Court, since 3 June 1992, about whether native title exists over a particular area. A determination is not automatically a recognition — 130 of them found that native title does not exist in the area the court considered.',
      sorts: ['date', 'name', 'area', 'juris'],
      defaultSort: 'date',
      defaultDir: -1,
      headers: [
        { label: 'Determination', key: 'name' },
        { label: 'What the court found' },
        { label: 'How it was decided' },
        { label: 'Jurisdiction', key: 'juris' },
        { label: 'Date', key: 'date', cls: 'num' },
        { label: 'Area', key: 'area', cls: 'num', tip: areaCaptionCommon },
      ],
      areaCaption: `${areaCaptionCommon} The determination area is also the area the court CONSIDERED, not the area over which native title was recognised.`,
      items: detItems,
    },
    claims: {
      id: 'claims',
      label: 'Registered claims',
      noun: 'registered claim',
      nouns: 'registered claims',
      blurb: 'The live applications that have met the registration test and are entered on the Register of Native Title Claims. Registration carries procedural rights; it is not a finding about the claim. Each of these also appears among the live applications below — the two registers overlap, and there are 189 distinct live matters in total.',
      sorts: ['date', 'name', 'area', 'juris'],
      defaultSort: 'date',
      defaultDir: -1,
      headers: [
        { label: 'Application', key: 'name' },
        { label: 'Registration test' },
        { label: 'Application type' },
        { label: 'Jurisdiction', key: 'juris' },
        { label: 'Lodged', key: 'date', cls: 'num' },
        { label: 'Area', key: 'area', cls: 'num', tip: areaCaptionCommon },
      ],
      areaCaption: `${areaCaptionCommon} These are areas APPLIED FOR, and nothing about them has been decided.`,
      items: claimItems,
    },
    applications: {
      id: 'applications',
      label: 'Live applications',
      noun: 'live application',
      nouns: 'live applications',
      blurb: 'The Schedule of Native Title Determination Applications: every application filed with the Federal Court and still live, whether or not it has met the registration test. Once a matter is determined it leaves this list, so no finished lodgement-to-determination duration can be read from it.',
      sorts: ['date', 'name', 'area', 'juris'],
      defaultSort: 'date',
      defaultDir: -1,
      headers: [
        { label: 'Application', key: 'name' },
        { label: 'Registration test' },
        { label: 'Application type' },
        { label: 'Jurisdiction', key: 'juris' },
        { label: 'Lodged', key: 'date', cls: 'num' },
        { label: 'Area', key: 'area', cls: 'num', tip: areaCaptionCommon },
      ],
      areaCaption: `${areaCaptionCommon} These are areas APPLIED FOR, and nothing about them has been decided.`,
      items: scheduleItems,
    },
    agreements: {
      id: 'agreements',
      label: 'Agreements',
      noun: 'agreement',
      nouns: 'agreements',
      blurb: 'Indigenous Land Use Agreements that are registered or in notification — voluntary, legally binding agreements about how country will be used, negotiated rather than decided.',
      sorts: ['date', 'name', 'area', 'juris'],
      defaultSort: 'date',
      defaultDir: -1,
      headers: [
        { label: 'Agreement', key: 'name' },
        { label: 'Type' },
        { label: 'Subject matter' },
        { label: 'Jurisdiction', key: 'juris' },
        { label: 'Registered', key: 'date', cls: 'num' },
        { label: 'Area', key: 'area', cls: 'num', tip: areaCaptionCommon },
      ],
      areaCaption: `${areaCaptionCommon} On this register the effect is enormous: the column sums to ${num(meta.areas.agreementNaiveSumKm2)} km², while the ground these agreements actually cover is ${num(meta.areas.agreementUnionKm2)} km².`,
      items: agreementItems,
    },
    corporations: {
      id: 'corporations',
      label: 'Corporations',
      noun: 'corporation',
      nouns: 'corporations',
      blurb: 'The registered native title bodies corporate that hold or manage native title after a determination. This list can only be ordered by name: ordering it by how much country a group holds would set Aboriginal and Torres Strait Islander groups against one another, which this site does not do.',
      sorts: ['name', 'juris'],
      defaultSort: 'name',
      defaultDir: 1,
      headers: [
        { label: 'Corporation', key: 'name' },
        { label: 'ICN' },
        { label: 'Determinations held' },
        { label: 'Jurisdictions', key: 'juris' },
        { label: 'Corporate register' },
      ],
      areaCaption: 'This register carries no area column. A corporation’s determinations are listed on its record card.',
      items: corporationItems,
    },
    regions: {
      id: 'regions',
      label: 'Representative regions',
      noun: 'representative region',
      nouns: 'representative regions',
      blurb: 'The 18 regions covered by a recognised representative body or service provider — the organisations that assist claimants and holders. Tasmania has no recognised body, and that is recorded on the register itself.',
      sorts: ['name', 'juris'],
      defaultSort: 'name',
      defaultDir: 1,
      headers: [
        { label: 'Region', key: 'name' },
        { label: 'Organisation' },
        { label: 'Kind' },
        { label: 'Jurisdiction', key: 'juris' },
        { label: 'Website' },
      ],
      areaCaption: 'This register carries no area column.',
      items: regionItems,
    },
  };

  const TYPE_ORDER: RecordType[] = ['determinations', 'claims', 'applications', 'agreements', 'corporations', 'regions'];

  /* ── state ── */

  const savedType = store(STORE_TYPE) as RecordType | null;
  let type: RecordType = savedType && TYPE_ORDER.includes(savedType) ? savedType : 'determinations';
  let sortKey: SortKey = DEFS[type].defaultSort;
  let dir: 1 | -1 = DEFS[type].defaultDir;
  const savedSort = store(STORE_SORT) as SortKey | null;
  if (savedSort && DEFS[type].sorts.includes(savedSort)) {
    sortKey = savedSort;
    dir = store(STORE_DIR) === 'asc' ? 1 : -1;
  }
  let query = '';
  let jurisFilter = '';
  let findings = new Set<Finding>(FINDING_ORDER);
  let limit = PAGE;
  let shown: Item[] = [];

  /* ── markup ── */

  host.innerHTML = `
    <div class="register-view">
      <div class="view-head">
        <h1>The record</h1>
        <p class="lede">Six public registers, searchable, in the words the Tribunal publishes them in.
          Open any row to read the whole record — every field written out, and a link to the judgment or
          the register that is the authority for it.</p>
      </div>

      <div class="reg-types" role="group" aria-label="Which register to read">
        ${TYPE_ORDER.map((t) => `<button type="button" class="reg-type" data-type="${t}" aria-pressed="${t === type}">
          <span>${esc(DEFS[t].label)}</span>
          <span class="mono reg-type-n">${esc(num(DEFS[t].items.length))}</span>
        </button>`).join('')}
      </div>
      <p class="small muted reg-blurb" id="reg-blurb"></p>

      <div class="reg-toolbar">
        <div class="reg-search">
          <label class="field-label" for="reg-q">Search this register</label>
          <input type="search" id="reg-q" placeholder="Name, case, holders, corporation, judge…"
            autocomplete="off" spellcheck="false">
        </div>
        <div class="control">
          <label class="field-label" for="reg-juris">Jurisdiction</label>
          <select id="reg-juris"></select>
        </div>
        <div class="control">
          <label class="field-label" for="reg-sort">Order by</label>
          <select id="reg-sort"></select>
        </div>
        <div class="control">
          <span class="field-label" aria-hidden="true">Direction</span>
          <button type="button" class="btn btn-sm" id="reg-dir"></button>
        </div>
      </div>

      <div class="chips" id="reg-findings" hidden></div>

      <p class="small reg-caption" id="reg-caption"></p>
      <p class="reg-count small muted" id="reg-count" role="status"></p>

      <div class="table-scroll" id="reg-scroll">
        <table class="reg-table">
          <thead><tr id="reg-head"></tr></thead>
          <tbody id="reg-body"></tbody>
        </table>
      </div>
      <div id="reg-empty"></div>
      <div class="reg-more" id="reg-more"></div>

      <div class="note legal caveat">
        <strong>Indicative boundaries. Not legal advice.</strong> Every area on this page is the register’s
        own indicative figure. These are not cadastral or legal boundaries, they cannot be used to establish
        rights over any particular parcel of land or water, and nothing here is legal advice. The Federal
        Court judgment linked from each determination, and the Tribunal’s own registers, are authoritative.
        Site-wide area figures are ${glossTerm('dissolved-union', 'dissolved unions')} measured in an
        ${glossTerm('equal-area-projection', 'equal-area projection')}, never a sum of this column.
      </div>
    </div>`;

  const typeButtons = [...host.querySelectorAll<HTMLButtonElement>('.reg-type')];
  const blurbEl = host.querySelector<HTMLElement>('#reg-blurb')!;
  const searchEl = host.querySelector<HTMLInputElement>('#reg-q')!;
  const jurisEl = host.querySelector<HTMLSelectElement>('#reg-juris')!;
  const sortEl = host.querySelector<HTMLSelectElement>('#reg-sort')!;
  const dirEl = host.querySelector<HTMLButtonElement>('#reg-dir')!;
  const findingsEl = host.querySelector<HTMLElement>('#reg-findings')!;
  const captionEl = host.querySelector<HTMLElement>('#reg-caption')!;
  const countEl = host.querySelector<HTMLElement>('#reg-count')!;
  const scrollEl = host.querySelector<HTMLElement>('#reg-scroll')!;
  const headEl = host.querySelector<HTMLElement>('#reg-head')!;
  const bodyEl = host.querySelector<HTMLElement>('#reg-body')!;
  const emptyEl = host.querySelector<HTMLElement>('#reg-empty')!;
  const moreEl = host.querySelector<HTMLElement>('#reg-more')!;

  const SORT_LABELS: Record<SortKey, string> = {
    date: 'Date', name: 'Name', area: 'Area', juris: 'Jurisdiction',
  };
  const dirLabel = (): string => {
    if (sortKey === 'date') return dir === -1 ? 'Newest first' : 'Oldest first';
    if (sortKey === 'area') return dir === -1 ? 'High to low' : 'Low to high';
    return dir === 1 ? 'A to Z' : 'Z to A';
  };

  function filtered(): Item[] {
    const def = DEFS[type];
    let list = def.items;
    if (jurisFilter) list = list.filter((i) => i.jurisAll.includes(jurisFilter));
    if (type === 'determinations' && findings.size < FINDING_ORDER.length) {
      list = list.filter((i) => (i.finding ? findings.has(i.finding) : false));
    }
    if (query) list = list.filter((i) => i.hay.includes(query));

    const has = (i: Item): boolean => {
      if (sortKey === 'date') return !!i.date;
      if (sortKey === 'area') return i.area != null;
      if (sortKey === 'juris') return !!i.juris;
      return true;
    };
    const byName = (a: Item, b: Item) => a.name.localeCompare(b.name, 'en-AU');
    const core = list.filter(has).sort((a, b) => {
      let c = 0;
      if (sortKey === 'date') c = (a.date ?? '').localeCompare(b.date ?? '');
      else if (sortKey === 'area') c = (a.area ?? 0) - (b.area ?? 0);
      else if (sortKey === 'juris') c = (a.juris ?? '').localeCompare(b.juris ?? '', 'en-AU');
      else c = byName(a, b);
      return c * dir || byName(a, b);
    });
    // Records with nothing in the sorted column always sit at the end, in both
    // directions — flipping them to the top would read as "these are the oldest".
    const rest = list.filter((i) => !has(i)).sort(byName);
    return [...core, ...rest];
  }

  // Rebuilding a <select> that currently has focus throws focus back to the
  // body, so the parts that depend only on the record type are rebuilt only
  // when the record type actually changes.
  let renderedType: RecordType | null = null;

  function paintControls(): void {
    const def = DEFS[type];
    for (const b of typeButtons) b.setAttribute('aria-pressed', String(b.dataset.type === type));

    if (renderedType !== type) {
      renderedType = type;
      blurbEl.textContent = def.blurb;
      captionEl.innerHTML = `<span class="reg-caption-mark" aria-hidden="true">▾</span> ${esc(def.areaCaption)}`;

      const counts = new Map<string, number>();
      for (const i of def.items) for (const c of i.jurisAll) counts.set(c, (counts.get(c) ?? 0) + 1);
      jurisEl.innerHTML = `<option value="">All jurisdictions (${num(def.items.length)})</option>${
        jurisOrder.map((j) => `<option value="${esc(j.code)}"${j.code === jurisFilter ? ' selected' : ''}>${esc(j.name)} (${num(counts.get(j.code) ?? 0)})</option>`).join('')}`;
      jurisEl.value = jurisFilter;

      sortEl.innerHTML = def.sorts.map((k) => `<option value="${k}">${esc(SORT_LABELS[k])}</option>`).join('');

      findingsEl.hidden = type !== 'determinations';
      if (type === 'determinations') {
        findingsEl.innerHTML = `<span class="xsmall muted">Show:</span>${FINDING_ORDER.map((f) => {
          const s = FINDING[f];
          const texture = f === 'parts'
            ? 'background-image:radial-gradient(rgba(255,255,255,.75) 1px, transparent 1px);background-size:4px 4px;'
            : '';
          return `<button type="button" class="pill" data-finding="${f}" aria-pressed="${findings.has(f)}"
            data-tip="${tipAttr(s.register, s.plain)}">
            <span class="legend-swatch reg-swatch" style="background:${s.fill};${texture}"></span>${esc(s.label)}</button>`;
        }).join('')}`;
      }
    }

    sortEl.value = sortKey;
    dirEl.textContent = dirLabel();
    dirEl.setAttribute('aria-label', `Reverse the order — currently ${dirLabel().toLowerCase()}`);

    // The sort control inside a sortable header is a REAL button, so Enter and
    // Space reach it without a keydown handler of our own. `aria-sort` stays on
    // the <th>, where assistive technology looks for it.
    headEl.innerHTML = def.headers.map((h) => {
      const cls = h.cls ? ` class="${h.cls}"` : '';
      const sorted = h.key === sortKey ? (dir === 1 ? 'ascending' : 'descending') : 'none';
      const tip = h.tip ? ` data-tip="${tipAttr(h.tip)}"` : '';
      const arrow = h.key === sortKey ? `<span class="reg-arrow" aria-hidden="true">${dir === 1 ? '▲' : '▼'}</span>` : '';
      const inner = h.key
        ? `<button type="button" class="reg-sort-btn" data-sort="${h.key}">${esc(h.label)}${arrow}</button>`
        : esc(h.label);
      return `<th scope="col"${cls}${h.key ? ` aria-sort="${sorted}"` : ''}${tip}>${inner}</th>`;
    }).join('');
  }

  function zeroExplanation(): string {
    const def = DEFS[type];
    const name = jurisFilter ? (jurisName.get(jurisFilter) ?? jurisFilter) : '';
    if (jurisFilter && !query && (jurisFilter === 'TAS' || jurisFilter === 'ACT') && type !== 'regions') {
      return `<div class="empty-state reg-zero">
        <p class="stat-value">0</p>
        <p><strong>Zero ${esc(def.nouns)} in ${esc(name)}.</strong></p>
        <p class="small">This is a real zero recorded by the register, not missing data. No determination has
          ever been made in ${esc(name)}, and no application, agreement or body corporate is recorded there.</p>
        <p class="reg-zero-actions">
          <button type="button" class="btn btn-sm" data-act="all-juris">Show every jurisdiction</button>
          <button type="button" class="btn btn-sm" data-act="show-region">Read the representative-region record for ${esc(name)}</button>
        </p></div>`;
    }
    return `<div class="empty-state">
      <p><strong>No ${esc(def.nouns)} match what you have asked for.</strong></p>
      <p class="small">${query ? `Nothing on this register contains “${esc(query)}”` : 'Nothing matches these filters'}${jurisFilter ? ` in ${esc(name)}` : ''}.</p>
      <p><button type="button" class="btn btn-sm" data-act="clear">Clear the search and filters</button></p></div>`;
  }

  function paintRows(): void {
    const def = DEFS[type];
    shown = filtered();
    const total = def.items.length;

    if (!shown.length) {
      scrollEl.hidden = true;
      moreEl.innerHTML = '';
      emptyEl.innerHTML = zeroExplanation();
      countEl.textContent = `No ${def.nouns} shown — ${plural(total, def.noun)} on this register in total.`;
      return;
    }

    scrollEl.hidden = false;
    emptyEl.innerHTML = '';
    const page = shown.slice(0, limit);
    bodyEl.innerHTML = page.map((item, i) => `<tr class="clickable" data-i="${i}">${item.cells(query)}</tr>`).join('');

    const bits = [`Showing ${num(page.length)} of ${plural(shown.length, def.noun)}`];
    if (shown.length !== total) bits.push(`filtered from ${num(total)}`);
    if (query) bits.push(`matching “${query}”`);
    if (jurisFilter) bits.push(`in ${jurisName.get(jurisFilter) ?? jurisFilter}`);
    countEl.textContent = `${bits.join(', ')}.`;

    const remaining = shown.length - page.length;
    moreEl.innerHTML = remaining > 0
      ? `<button type="button" class="btn" id="reg-more-btn">Show ${num(Math.min(PAGE, remaining))} more (${num(remaining)} still to come)</button>`
      : '';
    if (remaining > 0) {
      moreEl.querySelector<HTMLButtonElement>('#reg-more-btn')!.addEventListener('click', () => {
        limit += PAGE;
        paintRows();
      });
    }
  }

  function paintAll(): void {
    paintControls();
    paintRows();
  }

  /** A full rebuild, including the controls that normally survive a repaint. */
  function rebuildAll(): void {
    renderedType = null;
    paintAll();
  }

  /* ── events ── */

  for (const b of typeButtons) {
    b.addEventListener('click', () => {
      const next = b.dataset.type as RecordType;
      if (next === type) return;
      type = next;
      remember(STORE_TYPE, type);
      const def = DEFS[type];
      if (!def.sorts.includes(sortKey)) { sortKey = def.defaultSort; dir = def.defaultDir; }
      findings = new Set<Finding>(FINDING_ORDER);
      limit = PAGE;
      paintAll();
    });
  }

  let debounce = 0;
  searchEl.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      query = searchEl.value.trim().toLowerCase();
      limit = PAGE;
      paintRows();
    }, 300);
  });

  jurisEl.addEventListener('change', () => {
    jurisFilter = jurisEl.value;
    limit = PAGE;
    paintRows();
  });

  sortEl.addEventListener('change', () => {
    sortKey = sortEl.value as SortKey;
    dir = DEFS[type].defaultSort === sortKey ? DEFS[type].defaultDir : (sortKey === 'date' || sortKey === 'area' ? -1 : 1);
    remember(STORE_SORT, sortKey);
    remember(STORE_DIR, dir === 1 ? 'asc' : 'desc');
    limit = PAGE;
    paintAll();
  });

  dirEl.addEventListener('click', () => {
    dir = dir === 1 ? -1 : 1;
    remember(STORE_DIR, dir === 1 ? 'asc' : 'desc');
    limit = PAGE;
    paintAll();
  });

  const applyHeaderSort = (key: SortKey): void => {
    if (!DEFS[type].sorts.includes(key)) return;
    if (key === sortKey) dir = dir === 1 ? -1 : 1;
    else { sortKey = key; dir = key === 'date' || key === 'area' ? -1 : 1; }
    remember(STORE_SORT, sortKey);
    remember(STORE_DIR, dir === 1 ? 'asc' : 'desc');
    limit = PAGE;
    paintAll();
    // The header cell was just rewritten; put focus back where the reader left it.
    headEl.querySelector<HTMLElement>(`[data-sort="${sortKey}"]`)?.focus();
  };
  headEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-sort]');
    if (btn) applyHeaderSort(btn.dataset.sort as SortKey);
  });

  findingsEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('[data-finding]');
    if (!btn) return;
    const f = btn.dataset.finding as Finding;
    if (findings.has(f)) findings.delete(f); else findings.add(f);
    btn.setAttribute('aria-pressed', String(findings.has(f)));
    limit = PAGE;
    paintRows();
  });

  bodyEl.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest('a')) return;   // an outbound link does its own job
    const tr = target.closest<HTMLElement>('tr[data-i]');
    if (!tr) return;
    shown[Number(tr.dataset.i)]?.open();
  });

  emptyEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('[data-act]');
    if (!btn) return;
    if (btn.dataset.act === 'clear') {
      query = '';
      searchEl.value = '';
      jurisFilter = '';
      findings = new Set<Finding>(FINDING_ORDER);
      limit = PAGE;
      rebuildAll();
    } else if (btn.dataset.act === 'all-juris') {
      jurisFilter = '';
      limit = PAGE;
      rebuildAll();
    } else if (btn.dataset.act === 'show-region') {
      const code = jurisFilter;
      const r = ratsib.find((x) => x.juris === code);
      if (r) openRegion(r);
    }
  });

  paintAll();

  /* ── a shared link reopens what was shared ── */

  const shared = currentRoute().params.get('id');
  if (shared) {
    const det = getDetermination(shared);
    if (det) {
      openDetermination(det);
    } else {
      const bare = shared.replace(/^REG:/, '');
      const matter = matters.find((m) => m.id === bare && (shared.startsWith('REG:') ? m.onRegister : !m.onRegister))
        ?? matters.find((m) => m.id === bare);
      const agreement = agreementsFile.rows.find((a) => a.id === shared);
      const corp = holders.find((h) => `ICN${h.icn ?? h.name}` === shared);
      const region = ratsib.find((r) => (r.id ?? r.name) === shared);
      const jump = (next: RecordType): void => {
        type = next;
        remember(STORE_TYPE, type);
        paintAll();
      };
      if (matter) {
        jump(matter.onRegister ? 'claims' : 'applications');
        openMatter(matter);
      } else if (agreement) {
        jump('agreements');
        openAgreement(agreement);
      } else if (corp) {
        jump('corporations');
        openCorporation(corp);
      } else if (region) {
        jump('regions');
        openRegion(region);
      }
    }
  }
}
